import { useShowClinicFilter, useStudyClinics } from '@/hooks/use-clinics';
import { forgetClinicFilter, recallClinicFilter, rememberClinicFilter, } from '@/lib/clinicFilterMemory';
import { useCallback, useEffect } from 'react';
/**
 * Makes the worklist's clinic filter sticky for the tab session.
 *
 * Returns a drop-in replacement for the URL-state `setFilters` that records
 * clinic-filter changes: picking a clinic remembers it, explicitly clearing
 * it (back to "All clinics", or Clear-all) forgets it. On mount, when the
 * URL carries no clinic filter — every bare /study/list navigation — the
 * remembered clinic is re-applied once, provided it still exists in the
 * user's clinic list. Users without the clinic filter UI are untouched.
 */
export function useStickyClinicFilter(filters, setFilters) {
    const showClinicFilter = useShowClinicFilter();
    const { data: clinics } = useStudyClinics();
    // Re-apply whenever the URL has no clinic filter but one is remembered.
    // This never fights the user: an explicit clear (below) forgets the memory
    // BEFORE the URL changes, so the only way to arrive here with a remembered
    // clinic and a bare URL is a navigation reset — including the sidebar's
    // "Studies" item, which rewrites the URL without remounting this page.
    useEffect(() => {
        if (!showClinicFilter || !clinics || filters.clinicCode)
            return;
        const remembered = recallClinicFilter();
        if (remembered && clinics.some((c) => c.code === remembered)) {
            setFilters({ ...filters, clinicCode: remembered });
        }
    }, [showClinicFilter, clinics, filters, setFilters]);
    return useCallback((next) => {
        if (next.clinicCode) {
            rememberClinicFilter(next.clinicCode);
        }
        else if (filters.clinicCode) {
            // The clinic filter was set and this change drops it — an explicit
            // clear (bare navigations reset the URL without calling setFilters).
            forgetClinicFilter();
        }
        setFilters(next);
    }, [filters.clinicCode, setFilters]);
}
